import type { BaseItemSchema } from "@memorang/types";
import SuperJSON from "superjson";

type MinimalFile = { id: string; fileName: string; url: string };

type ItemMedia = {
	id?: string | number;
	url?: string;
	type?: string;
	mimeType?: string;
};

const IMAGE_EXTENSIONS = [
	".png",
	".jpg",
	".jpeg",
	".gif",
	".webp",
	".heic",
	".bmp",
];

const MIME_TO_EXTENSION: Record<string, string> = {
	"image/png": ".png",
	"image/jpeg": ".jpg",
	"image/jpg": ".jpg",
	"image/gif": ".gif",
	"image/webp": ".webp",
	"image/heic": ".heic",
	"image/bmp": ".bmp",
};

const extensionForMimeType = (mimeType?: string): string =>
	(mimeType && MIME_TO_EXTENSION[mimeType.toLowerCase()]) || ".png";

const hasImageExtension = (name: string): boolean => {
	const lower = name.toLowerCase().split("?")[0].split("#")[0];
	return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const urlBasename = (url: string): string => {
	const path = url.split("?")[0].split("#")[0];
	const last = path.split("/").pop() ?? "";
	return last || url;
};

const isImageMedia = (media: ItemMedia): boolean => {
	const type = media.type?.toLowerCase();
	if (type === "image" || type === "images") {
		return true;
	}
	if (media.mimeType?.toLowerCase().startsWith("image/")) {
		return true;
	}
	return Boolean(media.url && hasImageExtension(urlBasename(media.url)));
};

const toMinimalFile = (media: ItemMedia): MinimalFile | undefined => {
	if (!media.url) {
		return undefined;
	}
	const basename = urlBasename(media.url);
	const fileName = hasImageExtension(basename)
		? basename
		: `${basename}${extensionForMimeType(media.mimeType)}`;
	return { id: String(media.id ?? basename), fileName, url: media.url };
};

const extractImageFiles = (item: BaseItemSchema): MinimalFile[] => {
	const buckets: Array<ItemMedia[] | undefined> = [
		item.content?.stimulus?.media as ItemMedia[] | undefined,
		item.content?.media as ItemMedia[] | undefined,
		item.stimulus?.media as ItemMedia[] | undefined,
	];
	const seenUrls = new Set<string>();
	const files: MinimalFile[] = [];
	for (const bucket of buckets) {
		if (!bucket) {
			continue;
		}
		for (const media of bucket) {
			if (!isImageMedia(media)) {
				continue;
			}
			const file = toMinimalFile(media);
			if (file && !seenUrls.has(file.url)) {
				seenUrls.add(file.url);
				files.push(file);
			}
		}
	}
	return files;
};

export const buildAdditionalContextBody = (item: BaseItemSchema) => {
	const imageFiles = extractImageFiles(item);
	return imageFiles.length > 0
		? { additionalContext: SuperJSON.serialize({ files: imageFiles }) }
		: {};
};
